import React, {useEffect, useRef, useState} from 'react'
import {
  useContentStore,
  useGlobalStore,
  useProductStore,
  useBasketStore,
  usePromotionStore,
  useRapidDeliveryStore
} from '../../store/hooks/useStore'
import {Box, Container, Flex, Stack, Text} from '../../vanilla'
import {useMealDeals} from './use-meal-deals'
import {useMealDealsCompleteness} from './use-meal-deal-completeness'
import {usePageDataTracking} from '../../analytics/page-tracking'
import {Spinner} from '../../components/spinner'
import {Layout} from '../../components/layout'
import {Slot} from '../../components/cms/Slot'
import {SlotItem} from '../../types/cms'
import {MealDealItem} from './meal-deals-item'
import {Progress} from '../../types/meal-deals'
import {useMealDealTracking} from '../../analytics/meal-deal-tracking'
import {BreadcrumbsContent} from '../../components/cms/partials/breadcrumbsContent'
import {Breadcrumb} from '../../types/cms'
import {MEAL_DEAL_EXPLORER_KEY} from './meal-deal-explorer'
import {getFullDateOutput} from '../../utils/meal-deal-utils'
import {DefaultContentBody} from 'dc-delivery-sdk-js'
import {Nullable} from '../../types/utils'
import {flowResult} from 'mobx'
import {enoughProductsInPromotionGroup, filteredProductsIds} from '../../utils/product-utils'
import {observer} from 'mobx-react'
import {StatusMap} from '../../types/commerce'
import {NoAvailableDealsMessage} from './no-available-deals-message'
import {
  MealDealBuilderGroupState,
  MealDealBuilderState,
  resetBuilder
} from './meal-deal-builder-helper'
import {NormalizedPromotion, getPromoExpiryText } from '../../store/PromotionStore'
import {MealDealBuilderControls} from './meal-deal-builder-controls'
import {PartialProductItem} from '../../types/store/basket'
import {combineProductsWithExistingBasket} from '../../utils/utils'
import {useIntl} from 'react-intl'
import {MealDealCompletePrompt} from './meal-deal-complete-prompt'
import useNavigation from '../../hooks/use-navigation'
import {useCustomerStore} from '../../store/hooks/useStore'
import {useLocation} from 'react-router-dom'
import {redirectIfNotRegistered} from '../../utils/login-redirect-utils'
import {isMobile} from '../../utils/utils'
import {useGTMDispatch} from '../../analytics/react-gtm-hook'
import { LocationState } from '../../types/forms/login'
import { Seo } from '../../components/seo'
import { numbersToWords } from '../../utils/utils'
interface MealDealBuilderProps {
  promoID: string
}
interface MealDealProductCategory {
  id?: string
  name?: string
}

export const MealDealBuilder = observer(({promoID}: MealDealBuilderProps) => {
  const location = useLocation<LocationState>()
  const requiresRefetch = location?.state?.directedFrom === '/login'
  const {mealDeals, dealStatus} = useMealDeals(requiresRefetch)
  const {complete, update} = useMealDealsCompleteness(mealDeals?.groups)
  const {productsById, loading} = useProductStore()
  const [isLoading, setIsLoading] = useState(true)
  const sendPageData = usePageDataTracking()
  const sendMealDealData = useMealDealTracking()
  const pageTracked = useRef(false)
  const [breadCrumbs, setBreadCrumbs] = useState<Breadcrumb[]>([])
  const [mealDealBanner, setMealDealBanner] = useState<Nullable<DefaultContentBody>>(null)
  const [dealContentFetched, setDealContentFetched] = useState<boolean>(false)
  const [builderAvailable, setBuilderAvailable] = useState<boolean>(false)
  const {contentById, fetchItems} = useContentStore()
  const {selectedPriceDayOffset, selectedRegion, currency, selectedStoreId} = useGlobalStore()
  const {basket, status: basketStatus, updateBasketItemQuantity} = useBasketStore()
  const {isRapidPricingActive} = useRapidDeliveryStore()
  const BUILDER_PAGE_TYPE = 'Meal Deal Builder'
  const [builderState, setBuilderState] = useState<Nullable<MealDealBuilderState>>(null)
  const {formatNumber} = useIntl()
  const [mealDealAdded, setMealDealAdded] = useState(false)
  const navigate = useNavigation()
  const {isRegistered, loading: loadingCustomer} = useCustomerStore()
  const path = promoID ? `${location.pathname}?promo=${promoID}` : location.pathname
  const sendDataToGTM = useGTMDispatch()

  useEffect(() => {
    redirectIfNotRegistered({navigate, pathname: path, isRegistered, loadingCustomer})
  }, [loadingCustomer, isRegistered, path])

  useEffect(() => {
    if (dealStatus !== 'done' || loading) return
    async function setupMealDeal() {
      if (mealDeals && !dealContentFetched && isLoading) {
        setDealContentFetched(true)
        // Fetch content from amplience
        await flowResult(fetchItems([{key: mealDeals?.id}, {key: MEAL_DEAL_EXPLORER_KEY}]))
        // Set builder banner from response
        setMealDealBanner(contentById[mealDeals?.id] || null)
        // Setup breadcrumbs using amplience response
        setBreadCrumbs([])
        const content = contentById[MEAL_DEAL_EXPLORER_KEY]
        const crumbs: Breadcrumb[] = [...(content?.BreadCrumb || [])]
        const mealDealBuilderName = mealDeals?.name
        if (mealDealBuilderName) {
          crumbs.push({crumbText: mealDealBuilderName, crumbCtaLink: '#'})
        }
        setBreadCrumbs(crumbs)
      }

      // Only proceed to availability filtering if we have basket data
      if (basketStatus != StatusMap.IDLE) return

      const todaysDate = new Date()
      const startDate = mealDeals?.startDate ? new Date(mealDeals.startDate) : null
      const endDate = mealDeals?.endDate ? new Date(mealDeals.endDate) : null

      const validStartDate = !startDate || todaysDate >= startDate
      const validEndDate = !endDate || todaysDate < endDate

      const mealDealResponseIsValid =
        mealDeals != null &&
        mealDeals.c_isMealDealLanding === true &&
        mealDeals.c_customerIsEligible === true

      const dealHasValidProducts =
        Object.keys(productsById).length > 0 &&
        enoughProductsInPromotionGroup({
          promotion: mealDeals,
          productsIds: null,
          selectedPriceDayOffset: selectedPriceDayOffset ?? null,
          selectedRegion,
          productsById,
          selectedStoreId: selectedStoreId || '0',
          isRapidPricingActive
        }) === true

      const mealDealIsAvailable =
        mealDealResponseIsValid && dealHasValidProducts && validStartDate && validEndDate

      setBuilderAvailable(mealDealIsAvailable)

      if (!builderState && mealDealIsAvailable) {
        setBuilderState(new MealDealBuilderState(mealDeals as unknown as NormalizedPromotion))
      }

      setIsLoading(false)
    }

    setupMealDeal()
  }, [
    dealStatus,
    loading,
    mealDeals,
    mealDealBanner,
    fetchItems,
    contentById,
    isLoading,
    basket?.basketId,
    selectedRegion,
    selectedPriceDayOffset,
    productsById,
    basketStatus
  ])

  // Action page tracking once meal deals have loaded
  useEffect(() => {
    if (!isLoading) {
      // Action Tracking
      if (!pageTracked.current) {
        pageTracked.current = true
        sendPageData(BUILDER_PAGE_TYPE)
      }
    }
  }, [
    mealDeals,
    builderAvailable,
    loading,
    dealStatus,
    productsById,
    sendPageData,
    BUILDER_PAGE_TYPE
  ])

  // Scrolling logic - scroll to next incomplete step when a step is completed, and scroll to top when deal is added to basket
  const scrollToElement = (elementRef: HTMLDivElement | null) => {
    const headerHeight = document.querySelector('header')?.getBoundingClientRect().height || 0
    const topPosition = elementRef?.getBoundingClientRect().top || 0
    const bottomPosition = elementRef?.getBoundingClientRect().bottom || 0
    const windowHeight = window.innerHeight
    const desktopCenterView = () => {
      const centerPosition = windowHeight / 2 - (bottomPosition - topPosition)
      if (centerPosition > 0) {
        return centerPosition
      }
      return 0
    }
    const offset = isMobile() ? headerHeight + 8 : desktopCenterView()
    if (typeof window !== 'undefined' && elementRef) {
      try {
        window.scrollTo({top: topPosition + window.scrollY - offset, behavior: 'smooth'})
      } catch (e) {
        elementRef.scrollIntoView()
      }
    }
  }
  // Scroll to next uncompleted step when a step is completed
  const completedStepScroll = () => {
    if (
      builderState?.incompleteGroups &&
      builderState.incompleteGroups.length === builderState.groups.length
    )
      return
    if (builderState?.incompleteGroups && builderState.incompleteGroups.length > 0) {
      const nextIncompleteStep = builderState.incompleteGroups[0]
      if (stepRefs.current !== null) {
        scrollToElement(stepRefs.current[nextIncompleteStep])
      }
    }
  }

  // Always scroll to next step for single selection updates
  const singleSelectionSteps =
    builderState?.groups.filter((group) => group.requiredProductQuantity === 1) ?? []
  const singleSelectionProducts = singleSelectionSteps.flatMap((group) =>
    group.productItems.size > 0 ? Array.from(group.productItems.keys()) : []
  )
  const singleSelectionPreviouslySelected = useRef<string[]>(singleSelectionProducts)

  useEffect(() => {
    if (
      singleSelectionProducts.length > 0 &&
      JSON.stringify(singleSelectionProducts) !==
        JSON.stringify(singleSelectionPreviouslySelected.current)
    ) {
      completedStepScroll()
      singleSelectionPreviouslySelected.current = singleSelectionProducts
    }
  }, [singleSelectionProducts])

  // Scroll to the top when the meal deal is added to the basket
  useEffect(() => {
    if (mealDealAdded) {
      scrollToElement(builderRef.current)
    }
  }, [mealDealAdded])

  // Reset builder when basket changes to avoid inconsistencies between builder state and basket state
  useEffect(() => {
    /* Don't reset builder if we just added the meal deal to the basket to avoid a duplicate not started 
    event being sent to analytics */
    if (mealDealAdded) {
      return
    }
    resetBuilder(mealDeals as unknown as NormalizedPromotion, setBuilderState, update, builderRef)
    scrollToElement(stepRefs.current[0])
  }, [basket?.productItems])

  const allGroupProductIds = mealDeals?.groups?.flatMap((group) => group.ids) ?? []
  const stepCount = mealDeals?.groups?.length ?? 0
  const totalSelection = builderState?.totalSelected ?? 0
  const hasAnySelection = totalSelection > 0
  const remainingSteps = builderState?.stepsRemaining ?? stepCount

  const builderRef = useRef<HTMLDivElement | null>(null)
  const stepRefs = useRef<(HTMLDivElement | null)[]>([])
  const sentProgressRef = useRef<Progress | null>(null)

  const restartBuilder = () => {
    const promotion = mealDeals as unknown as NormalizedPromotion
    resetBuilder(promotion, setBuilderState, update, builderRef)
    scrollToElement(builderRef.current)
    setMealDealAdded(false)
  }

  let progress: Progress = Progress.NotStarted
  if (complete) {
    progress = Progress.Completed
  } else if (hasAnySelection) {
    progress = Progress.InProgress
  } else {
    progress = Progress.NotStarted
  }

  const finaliseMealDeal = async () => {
    const updatedBasket = await combineProductsWithExistingBasket(
      builderState?.selectedProducts ?? [],
      basket?.productItems ?? []
    )
    const productsToUpdate: PartialProductItem[] = updatedBasket.map((item) => ({
      product: {id: item.productId} as PartialProductItem['product'],
      quantity: item.quantity
    }))

    if (productsToUpdate.length === 0) {
      return
    }

    const response = await flowResult(updateBasketItemQuantity(productsToUpdate))
    if (response?.success === true) {
      analyticsTracking()
      setMealDealAdded(true)
    }
  }
  const analyticsTracking = () => {
    setTimeout(() => {
      try {
        const items =
          builderState?.selectedProducts.map((product) => {
            // Attempt to transform product categories into GA4 item_category format based on category tree.
            const productDetails = productsById[product.productId]
            const rawProductCategories: Array<Array<MealDealProductCategory>> =
              productDetails?.categories
            const itemCategories: {[key: string]: string} = {}
            // Use primary category, or fallback to first if no primary set.
            const primaryCategoryId =
              productDetails?.primaryCategoryId || rawProductCategories?.[0]?.[0]?.id

            if (productDetails && primaryCategoryId) {
              const categoryTree = rawProductCategories.find(
                (category: Array<MealDealProductCategory>) => {
                  return category?.[0]?.id === primaryCategoryId
                }
              )

              if (categoryTree) {
                categoryTree
                  .slice()
                  .reverse()
                  .forEach((category, index) => {
                    const suffix = index === 0 ? '' : `${index + 1}`
                    itemCategories[`item_category${suffix}`] = category.name ?? ''
                  })
              }
            }

            return {
              item_id: product.productId,
              item_name: productsById[product.productId]?.name,
              quantity: product.quantity,
              item_brand: productsById[product.productId]?.brand,
              ...itemCategories
            }
          }) || []

        const eventData = {
          event: 'add_to_cart',
          ecommerce: {
            item_location: 'Meal Deal Builder',
            value: Number(promoPrice),
            currency: 'GBP',
            items
          }
        }
        //@ts-ignore TS gets confused but the context should never be undefined when we get here.
        sendDataToGTM(eventData)
      } catch (error) {
        console.error(`Meal Deal Builder analytics tracking error: ${error.message}`)
      }
    }, 0)
  }

  useEffect(() => {
    if (
      isLoading ||
      dealStatus !== 'done' ||
      allGroupProductIds.length === 0 ||
      sentProgressRef.current === progress
    ) {
      return
    }

    sendMealDealData({
      id: mealDeals?.id,
      name: mealDeals?.name,
      progress,
      builderAvailable
    })

    // Update ref to prevent duplicate sends for the same progress state
    sentProgressRef.current = progress
  }, [dealStatus, hasAnySelection, progress, isLoading])
  const promotionStore = usePromotionStore()
  const promo = promotionStore.promotions[promoID]
  const promoPrice = promo?.price
  const formattedPrice = promoPrice
    ? formatNumber(Number(promoPrice), {style: 'currency', currency})
    : undefined
  const startDate = promo?.startDate ? new Date(promo.startDate) : undefined
  const endDate = promo?.endDate ? new Date(promo.endDate) : undefined
  const fullDateOutput = getFullDateOutput(startDate, endDate)
  const mealDealGroups = mealDeals?.groups ?? []

  if (isLoading) {
    return (
      <Flex justify="center" align="center" style={{minHeight: '200px'}}>
        <Spinner color="primary" />
      </Flex>
    )
  }

  if (!builderAvailable) {
    return (
      <Layout>
        <Stack paddingBottom={['28px', '36px']}>
          <Container paddingX={['0px', '8px']} paddingY="16px">
            <NoAvailableDealsMessage isExplorer={false} />
          </Container>
        </Stack>
      </Layout>
    )
  }

  const metaDescription = () => {
    const selectionText = mealDealGroups.map((group, index) =>
      group.label ? `${numbersToWords(group.req)} ${group.label}${index === mealDealGroups.length - 2 ? ' and' : index === mealDealGroups.length - 1 ? '.' : ','}` : undefined
    ).filter(Boolean).join(' ')
    return `${selectionText.length > 0 ? 'Pick ' + selectionText + ' ' : ''}${getPromoExpiryText(promo.startDate, promo.endDate)}`
  }

  return (
    <>
      <Seo
        title={mealDeals?.name}
        description={metaDescription()}
      />
      <Layout>
        <Container padding="16px">
          {breadCrumbs.length > 0 && <BreadcrumbsContent breadCrumb={breadCrumbs} />}
          <Box display="block" marginBottom="24px">
            {mealDealBanner && (
              <Slot
                data={mealDealBanner as SlotItem}
                deliveryKey={mealDealBanner._meta.deliveryKey}
              />
            )}
          </Box>
          <Flex
            ref={builderRef}
            direction="column"
            justifyContent="center"
            alignItems={['flex-start', 'center']}
            gap="16px"
            marginBottom={['20px', '28px']}
          >
            <Text variant="heading5" data-test-selector="meal-deal-title">
              {mealDeals?.name}
            </Text>
            <Text variant="text1">{fullDateOutput}</Text>
            {mealDeals?.details && <Text variant="text3">{mealDeals.details}</Text>}
          </Flex>
          {mealDealAdded ? (
            <Flex justifyContent="center" alignItems="center">
              <MealDealCompletePrompt
                resetBuilder={() => {
                  restartBuilder()
                }}
              />
            </Flex>
          ) : (
            <>
              <Text
                variant="heading5"
                marginTop={['8px', '40px']}
                marginBottom={['20px', '28px']}
                alignSelf="flex-start"
              >
                Create Your Deal
              </Text>

              <Stack gap={['20px', '28px']} marginBottom="28px" id={mealDeals?.id}>
                {mealDeals?.groups?.map?.((group, index) => (
                  <MealDealItem
                    index={index}
                    stepRef={(el: HTMLDivElement) => (stepRefs.current[index] = el)}
                    key={`${mealDeals?.id}-${group.label ?? 'step'}-${index}`}
                    label={group.label}
                    steps={stepCount}
                    currentBasket={
                      basket?.productItems?.map((prod) => ({
                        productId: prod.productId,
                        quantity: prod.quantity
                      })) ?? []
                    }
                    productsIds={filteredProductsIds({
                      promotion: mealDeals,
                      productsIds: group?.ids,
                      selectedPriceDayOffset: selectedPriceDayOffset ?? null,
                      selectedRegion: selectedRegion,
                      productsById: productsById,
                      selectedStoreId: selectedStoreId || '0',
                      isRapidPricingActive
                    })}
                    req={group.req}
                    verb={group.verb}
                    setCompleteness={(complete) => {
                      update(index, complete), complete ? completedStepScroll() : null
                    }}
                    groupState={builderState?.groups[index] as MealDealBuilderGroupState}
                  />
                ))}
              </Stack>
              <MealDealBuilderControls
                remainingSteps={remainingSteps}
                mealDealPrice={formattedPrice}
                onReset={() => {
                  restartBuilder()
                }}
                onAddDealToBasket={() => {
                  finaliseMealDeal()
                }}
                hasAnySelection={hasAnySelection}
              />
            </>
          )}
        </Container>
      </Layout>
    </>
  )
})
